import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import { ChevronLeft, ChevronRight, Clock, MapPin, AlertTriangle, Route as RouteIcon } from 'lucide-react';
import { request } from '../../api/client';
import { Drawer, Tag, cn } from '../../ui';
import type { TagTone } from '../../ui';
import { formatIstTime } from '../../lib/time';
import { usePermission } from '../../store/auth';
import { RouteMapModal } from '../../components/RouteMapModal';

type Row = {
  id: string; logDate: string; status: string; userId: string;
  checkInAt?: string | null; checkOutAt?: string | null; workedMin?: number | null;
  isWithinGeofence?: boolean | null; anomalyFlags?: string[] | null; synthetic?: boolean;
  lastLat?: string | null; lastLng?: string | null; lastActivityAt?: string | null; lastActivityType?: string | null;
};
export type AttUser = { id: string; name?: string | null; role?: string | null };

const fmtWorked = (min?: number | null) => (min == null ? '—' : `${Math.floor(min / 60)}h ${min % 60}m`);
const mapsUrl = (lat?: string | null, lng?: string | null) => (lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : undefined);

// Calendar cell tint per status.
const STATUS_CELL: Record<string, string> = {
  PRESENT: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/35 dark:text-emerald-300',
  HALF_DAY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/35 dark:text-amber-300',
  LEAVE: 'bg-blue-100 text-blue-700 dark:bg-blue-900/35 dark:text-blue-300',
  ABSENT: 'bg-red-100 text-red-700 dark:bg-red-900/35 dark:text-red-300',
};
const STATUS_TAG: Record<string, TagTone> = { PRESENT: 'green', HALF_DAY: 'gold', LEAVE: 'blue', ABSENT: 'red' };
const HOLIDAY_CELL = 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300';

/**
 * Per-user attendance history in a slide-over: a month calendar heat-map (present / absent / leave /
 * half-day / holiday / weekly-off), a summary strip, and the day-by-day records. Reuses the same
 * `/attendance?userId=…&absent=1` feed the list uses (synthetic absents included), so it's presentation-only.
 */
export function AttendanceUserDrawer({ user, onClose }: { user: AttUser | null; onClose: () => void }) {
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));
  const canRoute = usePermission('DASH.SALES'); // gate the movement-route view (same perm as the route endpoint)
  const [routeDate, setRouteDate] = useState<string | null>(null); // a clicked day → open its route map
  useEffect(() => { if (user) setMonth(dayjs().startOf('month')); }, [user?.id]);

  const from = month.format('YYYY-MM-DD');
  const to = month.endOf('month').format('YYYY-MM-DD');
  const { data, isLoading } = useQuery({
    queryKey: ['attendance-user', user?.id, from],
    queryFn: () => request<Row[]>({ url: '/attendance', params: { userId: user!.id, from, to, absent: '1' } }),
    enabled: !!user,
  });
  // Holidays that apply to THIS rep — national + their state's regional ones (resolved server-side
  // from all the rep's territories). A holiday reads like the Sunday weekly-off, never an absence.
  const { data: holidays } = useQuery({
    queryKey: ['holidays-applicable', user?.id, from],
    queryFn: () => request<Array<{ holidayDate: string; name: string }>>({
      url: '/holidays/applicable', params: { userId: user!.id, from, to },
    }),
    enabled: !!user,
  });

  const byDate = useMemo(() => {
    const m = new Map<string, Row>();
    for (const r of data ?? []) if (!m.has(r.logDate)) m.set(r.logDate, r);
    return m;
  }, [data]);
  const holidayName = useMemo(
    () => new Map((holidays ?? []).map((h) => [String(h.holidayDate), h.name])),
    [holidays],
  );
  // A synthetic "Absent" that lands on an applicable holiday is really an off-day, not an absence.
  const isHolidayAbsent = (r: Row) => r.synthetic && r.status === 'ABSENT' && holidayName.has(r.logDate);

  const stats = useMemo(() => {
    let present = 0, absent = 0, leave = 0, half = 0, worked = 0;
    for (const r of byDate.values()) {
      if (isHolidayAbsent(r)) continue; // a holiday isn't an absence — don't count it either way
      if (r.status === 'PRESENT') present++;
      else if (r.status === 'ABSENT') absent++;
      else if (r.status === 'LEAVE') leave++;
      else if (r.status === 'HALF_DAY') half++;
      if (r.workedMin) worked += r.workedMin;
    }
    const expected = present + absent + half + leave; // days the rep was expected in (excludes off days)
    const pct = expected ? Math.round(((present + half * 0.5) / expected) * 100) : 0;
    return { present, absent, leave, half, worked, pct };
  }, [byDate, holidayName]);

  const today = dayjs();
  const cells = useMemo(() => {
    const lead = month.startOf('month').day(); // 0 = Sunday
    const out: Array<{ d: number | null; date?: Dayjs; ds?: string }> = [];
    for (let i = 0; i < lead; i++) out.push({ d: null });
    for (let d = 1; d <= month.daysInMonth(); d++) { const date = month.date(d); out.push({ d, date, ds: date.format('YYYY-MM-DD') }); }
    return out;
  }, [month]);

  const cellInfo = (date: Dayjs, ds: string): { cls: string; title: string } => {
    const row = byDate.get(ds);
    // A REAL punch / leave / half-day always shows its own status (even on a holiday — informative).
    if (row && !row.synthetic && row.status && STATUS_CELL[row.status]) {
      const io = [row.checkInAt && `in ${formatIstTime(row.checkInAt)}`, row.checkOutAt && `out ${formatIstTime(row.checkOutAt)}`].filter(Boolean).join(' · ');
      return { cls: STATUS_CELL[row.status], title: `${row.status.replace('_', ' ')}${io ? ` · ${io}` : ''}` };
    }
    // Applicable holiday — reads like the weekly-off; overrides a synthetic "Absent".
    const hol = holidayName.get(ds);
    if (hol) return { cls: HOLIDAY_CELL, title: `Holiday · ${hol}` };
    if (date.day() === 0) return { cls: 'bg-surface-2 text-subtle', title: 'Weekly off (Sunday)' };
    if (row?.status && STATUS_CELL[row.status]) return { cls: STATUS_CELL[row.status], title: row.status.replace('_', ' ') };
    if (date.isAfter(today, 'day')) return { cls: 'text-subtle', title: '' };
    return { cls: 'text-subtle', title: 'No record' };
  };

  const records = useMemo(() => [...byDate.values()].sort((a, b) => b.logDate.localeCompare(a.logDate)), [byDate]);

  return (
    <>
    <Drawer
      open={!!user} onClose={onClose} width={640}
      title={user ? `${user.name ?? `#${user.id}`}${user.role ? ` · ${user.role}` : ''}` : 'Attendance'}
    >
      {/* Month switcher */}
      <div className="flex items-center justify-between">
        <span className="type-body-strong text-content">Attendance history</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setMonth((m) => m.subtract(1, 'month'))}
            className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:bg-surface-2 hover:text-content">
            <ChevronLeft className="size-4" />
          </button>
          <span className="w-32 text-center type-body-medium text-content">{month.format('MMMM YYYY')}</span>
          <button type="button" onClick={() => setMonth((m) => m.add(1, 'month'))} disabled={month.isSame(today, 'month')}
            className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:bg-surface-2 hover:text-content disabled:opacity-40 disabled:hover:bg-transparent">
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Stat label="Present" value={stats.present} tone="text-emerald-600 dark:text-emerald-400" />
        <Stat label="Absent" value={stats.absent} tone="text-red-600 dark:text-red-400" />
        <Stat label="Leave" value={stats.leave} tone="text-blue-600 dark:text-blue-400" />
        <Stat label="Half-day" value={stats.half} tone="text-amber-600 dark:text-amber-400" />
        <Stat label="Attendance" value={`${stats.pct}%`} tone="text-content" />
        <Stat label="Worked" value={fmtWorked(stats.worked)} tone="text-content" />
      </div>

      {/* Calendar heat-map */}
      <div className="mt-5 rounded-card border border-line p-3">
        <div className="mb-1.5 grid grid-cols-7 gap-1 text-center type-overline text-muted">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <div key={i}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (c.d == null) return <div key={i} />;
            const info = cellInfo(c.date!, c.ds!);
            const isToday = c.date!.isSame(today, 'day');
            const row = byDate.get(c.ds!);
            // The day has field movement to show if there's a real punch/activity that carried a location.
            const clickable = canRoute && !!(row && !row.synthetic && (row.checkInAt || row.lastActivityAt));
            const cls = cn('relative flex h-10 items-center justify-center rounded-control text-sm tabular-nums', info.cls,
              isToday && 'ring-2 ring-brand-500 ring-offset-1 ring-offset-surface',
              clickable && 'cursor-pointer transition hover:ring-2 hover:ring-brand-400');
            const dot = clickable ? <span className="absolute bottom-0.75 left-1/2 size-1 -translate-x-1/2 rounded-full bg-brand-500" /> : null;
            return clickable ? (
              <button key={i} type="button" title={`${info.title} · click for route`} onClick={() => setRouteDate(c.ds!)} className={cls}>{c.d}{dot}</button>
            ) : (
              <div key={i} title={info.title} className={cls}>{c.d}</div>
            );
          })}
        </div>
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-2xs text-muted">
          <Legend cls="bg-emerald-100 dark:bg-emerald-900/35" label="Present" />
          <Legend cls="bg-red-100 dark:bg-red-900/35" label="Absent" />
          <Legend cls="bg-blue-100 dark:bg-blue-900/35" label="Leave" />
          <Legend cls="bg-amber-100 dark:bg-amber-900/35" label="Half-day" />
          <Legend cls={HOLIDAY_CELL} label="Holiday" />
          <Legend cls="bg-surface-2" label="Weekly off" />
        </div>
      </div>

      {/* Records */}
      <div className="mt-5">
        <div className="mb-2 type-body-strong text-content">Records</div>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted">Loading…</div>
        ) : records.length === 0 ? (
          <div className="rounded-card border border-line py-6 text-center text-sm text-muted">No attendance recorded this month.</div>
        ) : (
          <div className="divide-y divide-line overflow-hidden rounded-card border border-line">
            {records.map((r) => {
              const holName = isHolidayAbsent(r) ? holidayName.get(r.logDate) : undefined;
              return (
                <div key={r.id} className="flex items-center gap-3 px-3 py-2.5 text-sm">
                  <span className="w-24 shrink-0 font-medium text-content">{dayjs(r.logDate).format('ddd, DD MMM')}</span>
                  {holName
                    ? <Tag tone="purple">Holiday</Tag>
                    : <Tag tone={STATUS_TAG[r.status] ?? 'neutral'}>{r.status.replace('_', ' ')}</Tag>}
                  <span className="flex-1 truncate text-muted">
                    {holName ? (
                      holName
                    ) : (
                      <>
                        {r.checkInAt && (
                          <><Clock className="mr-1 inline size-3.5" />{formatIstTime(r.checkInAt)}{r.checkOutAt ? `–${formatIstTime(r.checkOutAt)}` : ' · open'}</>
                        )}
                        {r.workedMin != null && r.workedMin > 0 && <span className="ml-2">· {fmtWorked(r.workedMin)}</span>}
                      </>
                    )}
                  </span>
                  {!holName && canRoute && !r.synthetic && (r.checkInAt || r.lastActivityAt) ? (
                    <button type="button" onClick={() => setRouteDate(r.logDate)}
                      className="inline-flex shrink-0 items-center gap-1.5 type-label text-brand-700! hover:underline dark:text-brand-400!"
                      title="View this day's route">
                      <RouteIcon className="size-3.5" /> Route
                    </button>
                  ) : (!holName && mapsUrl(r.lastLat, r.lastLng) && (
                    <a href={mapsUrl(r.lastLat, r.lastLng)} target="_blank" rel="noopener"
                      className="inline-flex shrink-0 items-center gap-1 type-label text-brand-700! hover:underline dark:text-brand-400!"
                      title={`Last activity: ${r.lastActivityType ?? 'location'}${r.lastActivityAt ? ` · ${formatIstTime(r.lastActivityAt)}` : ''} — open in Google Maps`}>
                      <MapPin className="size-3.5" /> Map
                    </a>
                  ))}
                  {!holName && r.isWithinGeofence === false && <Tag tone="red"><MapPin className="mr-0.5 inline size-3" />Out</Tag>}
                  {!holName && (r.anomalyFlags?.length ?? 0) > 0 && <Tag tone="red"><AlertTriangle className="mr-0.5 inline size-3" />{r.anomalyFlags!.length}</Tag>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Drawer>
    {user && routeDate && (
      <RouteMapModal spId={user.id} name={user.name ?? `#${user.id}`} from={routeDate} to={routeDate} onClose={() => setRouteDate(null)} />
    )}
    </>
  );
}

function Stat({ label, value, tone }: { label: string; value: ReactNode; tone: string }) {
  return (
    <div className="rounded-control border border-line bg-surface-2/40 px-2 py-2 text-center">
      <div className={cn('type-metric-sm', tone)}>{value}</div>
      <div className="text-2xs text-muted">{label}</div>
    </div>
  );
}
function Legend({ cls, label }: { cls: string; label: string }) {
  return <span className="inline-flex items-center gap-1"><span className={cn('inline-block size-3 rounded-sm border border-line', cls)} />{label}</span>;
}
